import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Return {
  id: string
  order_id: string
  customer_name: string
  customer_email: string
  reason_detail: string
  status: string
  return_type: string
  order_amount: number
  refund_amount: number | null
  created_at: string
}

export default function ReturnsRefundsPage() {
  const [returns, setReturns] = useState<Return[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [returnsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/returns/'),
      fetch(API_BASE + '/returns/statistics')
    ])
    if (returnsRes.ok) setReturns((await returnsRes.json()).returns || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'refunded': return 'bg-green-100 text-green-800'
      case 'approved': return 'bg-blue-100 text-blue-800'
      case 'received': return 'bg-indigo-100 text-indigo-800'
      case 'inspecting': return 'bg-yellow-100 text-yellow-800'
      case 'requested': return 'bg-gray-100 text-gray-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Returns & Refunds</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Process Return</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Returns</div>
              <div className="text-2xl font-bold">{statistics.total_returns}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.pending_returns}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Refunded</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_refunded_amount)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Return Rate</div>
              <div className="text-2xl font-bold">{statistics.return_rate}%</div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Returns</h2></div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Return ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {returns.slice(0, 15).map(ret => (
                <tr key={ret.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-mono text-sm">{ret.id}</div>
                    <div className="text-xs text-gray-500">{ret.order_id}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium">{ret.customer_name}</div>
                    <div className="text-sm text-gray-500">{ret.customer_email}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">{ret.reason_detail}</div>
                    <div className="text-xs text-gray-500 capitalize">{ret.return_type}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium">{formatCurrency(ret.order_amount)}</div>
                    {ret.refund_amount && (
                      <div className="text-xs text-green-600">Refund: {formatCurrency(ret.refund_amount)}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(ret.status)}`}>
                      {ret.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  )
}
